import { useEffect } from "react";
import { Modal, Form, Input, Select, Button, DatePicker } from "antd";
import dayjs from "dayjs";
import type { PlanMilestone, MilestoneEventType } from "../types";

interface MilestoneEditorProps {
  open: boolean;
  milestone: PlanMilestone | null;
  onClose: () => void;
  onSave: (updated: PlanMilestone) => void;
  onAdd?: (milestone: Omit<PlanMilestone, "id">) => void;
  mode: "edit" | "add";
}

const EVENT_TYPE_OPTIONS: { value: MilestoneEventType; label: string }[] = [
  { value: "Planned Date", label: "Planned Date" },
  { value: "Target", label: "Target" },
  { value: "Forecast", label: "Forecast" },
];

export default function MilestoneEditor({
  open,
  milestone,
  onClose,
  onSave,
  onAdd,
  mode,
}: MilestoneEditorProps) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (milestone) {
        form.setFieldsValue({
          event_name: milestone.event_name,
          event_type: milestone.event_type,
          start_date: milestone.start_date ? dayjs(milestone.start_date) : null,
          end_date: milestone.end_date ? dayjs(milestone.end_date) : null,
        });
      } else {
        form.resetFields();
        form.setFieldValue("event_type", "Planned Date");
      }
    }
  }, [open, milestone, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const startDate = values.start_date
        ? dayjs(values.start_date).format("YYYY-MM-DD")
        : "";
      const endDate = values.end_date
        ? dayjs(values.end_date).format("YYYY-MM-DD")
        : "";

      if (mode === "edit" && milestone) {
        onSave({
          ...milestone,
          event_name: values.event_name,
          event_type: values.event_type,
          start_date: startDate,
          end_date: endDate || startDate,
        });
      } else if (mode === "add" && onAdd) {
        onAdd({
          event_name: values.event_name,
          event_type: values.event_type,
          start_date: startDate,
          end_date: endDate || startDate,
        });
      }
      form.resetFields();
    } catch {
      // validation error
    }
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Milestone" : "Add Milestone"}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          className="bg-[#0C98D0] border-none"
          onClick={handleOk}
        >
          {mode === "edit" ? "Save" : "Add"}
        </Button>,
      ]}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="event_name"
          label="Milestone Name"
          rules={[{ required: true, message: "Milestone name is required" }]}
        >
          <Input placeholder="e.g. Site Activation" />
        </Form.Item>
        <Form.Item name="event_type" label="Type">
          <Select options={EVENT_TYPE_OPTIONS} />
        </Form.Item>
        <div className="flex gap-3">
          <Form.Item name="start_date" label="Start Date" className="flex-1">
            <DatePicker className="w-full" format="MMM D, YYYY" />
          </Form.Item>
          <Form.Item name="end_date" label="End Date" className="flex-1">
            <DatePicker className="w-full" format="MMM D, YYYY" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
